import isUndefinedNullOrEmpty from '@ahd/shared/tools/is-undefined-null-or-empty';
import outputConsole from '@ahd/shared/tools/output-console';

import { type Components, createTheme, type Theme, type TypographyVariantsOptions } from '@mui/material/styles';

import { type ConfigObject, DebugLevel } from '../_configs/types';
import merge from '../utils/tools/merge';
import replaceStyleTexts from '../utils/tools/replaceStyleTexts';
import { breakpoints } from './breakpoints';
import { componentsOverrides } from './overrides';
import { palette } from './palette';
import { shadows } from './shadows';
import { typography } from './typography';

export default function initTheme(settings: ConfigObject, rootContainer: HTMLDivElement, forceDark: boolean): Theme {
    const themeMode = forceDark || settings?.style?.darkMode ? 'dark' : 'light';

    const t = createTheme({
        ...palette(themeMode, rootContainer, settings?.style?.primaryColor, settings?.style?.secondaryColor),
        breakpoints: {
            values: breakpoints,
        },
        direction: 'ltr',
        rawTypography: typography(true, settings?.style?.fontSize),
        shadows: shadows(themeMode),
        shape: { borderRadius: settings?.style?.borderRadius * 4 },
        typography: typography(false, settings?.style?.fontSize),
        zIndex: {
            modal: 99999999999,
            tooltip: 99999999999,
        },
    });

    t.typography = merge(
        {},
        t.typography,
        replaceStyleTexts<TypographyVariantsOptions>(settings?.style?.muiTypography, {
            captionFontSize: t?.rawTypography?.caption?.fontSize,
            colorPrimary: settings?.style?.primaryColor,
            colorSecondary: settings?.style?.secondaryColor,
            fontFamily: settings?.style?.fontFamily,
            fontSize: settings?.style?.fontSize || 16,
            parentBackgroundColor: t?.parentBackgroundColor,
            textPrimary: t?.palette?.text?.primary,
        })
    );

    t.components = merge(
        {},
        componentsOverrides(t),
        replaceStyleTexts<Components<Theme>>(settings?.style?.muiComponents, {
            captionFontSize: t?.rawTypography?.caption?.fontSize,
            colorPrimary: settings?.style?.primaryColor,
            colorSecondary: settings?.style?.secondaryColor,
            fontFamily: settings?.style?.fontFamily,
            fontSize: settings?.style?.fontSize || 16,
            parentBackgroundColor: t?.parentBackgroundColor,
            textPrimary: t?.palette?.text?.primary,
        })
    );

    if (isUndefinedNullOrEmpty(t.typography.fontFamily)) {
        t.typography.body1.fontFamily = settings?.style?.fontFamily;
        t.typography.body2.fontFamily = settings?.style?.fontFamily;
        t.typography.button.fontFamily = settings?.style?.fontFamily;
    }

    if (settings?.general?.debug === DebugLevel.TRACKING || settings?.general?.debug >= DebugLevel.INFO) {
        outputConsole('log', rootContainer?.id, `theme`, t);
    }

    return t;
}
